import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, merge, Observable, of, Subscription } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { ApiModel } from 'models';

import { AuthenticatedUserService } from 'services/authenticatedUser';

interface ProductsVisibilityState {
    isAutoRestockVisible: boolean;
    isSuggestionsVisible: boolean;
    isInsightsVisible: boolean;
}

@Injectable()
export class ProductsVisibilityService implements OnDestroy {
    private authenticatedUserSubscriber: Subscription;
    private productsVisibilitySubscriber: Subscription;
    private stateSubject = new BehaviorSubject<ProductsVisibilityState>({
        isAutoRestockVisible: null,
        isSuggestionsVisible: null,
        isInsightsVisible: null
    });

    public state: Observable<ProductsVisibilityState> = this.stateSubject.asObservable().pipe(shareReplay(1));

    constructor(
        private authenticatedUserService: AuthenticatedUserService,
        private apiModel: ApiModel
    ) {
        this.authenticatedUserSubscriber = this.authenticatedUserService.isLoggedIn.subscribe((isLoggedIn) => {
            if (!isLoggedIn) {
                this.stateSubject.next({
                    isAutoRestockVisible: false,
                    isSuggestionsVisible: false,
                    isInsightsVisible: false
                });
                return;
            }

            this.productsVisibilitySubscriber = merge(
                this.isSuggestionsProductVisible(),
                this.isAutoRestockProductVisible(),
                this.isInsightsProductVisible()
            ).subscribe((newState) => this.stateSubject.next({ ...this.stateSubject.getValue(), ...newState }));
        });
    }

    ngOnDestroy(): void {
        this.authenticatedUserSubscriber.unsubscribe();
        this.productsVisibilitySubscriber.unsubscribe();
    }

    private isSuggestionsProductVisible(): Observable<{ isSuggestionsVisible: boolean }> {
        return this.apiModel
            .makeHttpObservableRequest<{ allowed: boolean }>({
                method: 'GET',
                url: `${this.apiModel.getSuggestionEndpoint()}/allowed`
            })
            .pipe(
                map(({ allowed }) => ({ isSuggestionsVisible: allowed })),
                catchError((error) => {
                    console.error('ProductsVisibilityService: failed to fetch suggestions visibility', error);
                    return of({ isSuggestionsVisible: false });
                })
            );
    }

    private isAutoRestockProductVisible(): Observable<{ isAutoRestockVisible: boolean }> {
        return this.apiModel
            .makeHttpObservableRequest<{ allowed: boolean }>({
                method: 'GET',
                url: `${this.apiModel.getAutoRestockEndpoint()}/allowed`
            })
            .pipe(
                map(({ allowed }) => ({ isAutoRestockVisible: allowed })),
                catchError((error) => {
                    console.error('ProductsVisibilityService: failed to fetch auto restock visibility', error);
                    return of({ isAutoRestockVisible: false });
                })
            );
    }

    private isInsightsProductVisible(): Observable<{ isInsightsVisible: boolean }> {
        return this.apiModel
            .makeHttpObservableRequest<{ allowed: boolean }>({
                method: 'GET',
                url: `${this.apiModel.getInsightsEndpoint()}/allowed`
            })
            .pipe(
                map(({ allowed }) => ({ isInsightsVisible: allowed })),
                catchError((error) => {
                    console.error('ProductsVisibilityService: failed to fetch insights visibility', error);
                    return of({ isInsightsVisible: false });
                })
            );
    }
}
